import React, { useContext, useState } from 'react';
import {
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    InputAdornment,
    IconButton,
    Box,
    CssBaseline, // Optional: for baseline dark mode styles
    Switch,       // Import Switch
    FormControlLabel, // Import FormControlLabel for Switch labeling
    FormControl,
    Select,
    MenuItem,
    FormHelperText,
    InputLabel,
    useTheme
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy'; // Example icon
import { WalletsContext } from '../../state/context/wallets.context';

function WalletManagerForm() {
    const { addWallet } = useContext(WalletsContext);

    const [walletName, setWalletName] = useState('');
    const [walletKey, setWalletKey] = useState('');
    const [type, setType] = useState('');
    const [generateMode, setGenerateMode] = useState(false); // false = Track, true = Generate

    const wallets = useContext(WalletsContext).state.wallets || [];

    const handleToggleChange = (event) => {
        setGenerateMode(event.target.checked);
        // Optionally clear address field when switching to generate mode
        if (event.target.checked) {
            setWalletKey('');
        }
    };

    const handleSubmit = () => {
        if (wallets.length >= 3 && process.env.NODE_ENV === "production") {
            alert("You can only create 3 wallets at a time");
            return;
        }
        if (generateMode) {
            console.log('Generating wallet with name:', walletName);
            addWallet({ name: walletName, type });
        } else {
            console.log('Adding wallet:', { name: walletName, key: "****" });
            addWallet({ name: walletName, key: walletKey, type });
        }
    };

    const handlePaste = async () => {
        try {
            const text = await navigator.clipboard.readText();
            setWalletKey(text);
            console.log('Pasted:', text);
        } catch (err) {
            console.error('Failed to read clipboard contents: ', err);
        }
    };


    return (

        <Card
            sx={{
                maxWidth: 400, // Adjust width as needed
                margin: 'auto', // Center the card
                mt: 5, // Margin top for spacing
                borderRadius: 3, // Rounded corners
                // bgcolor: 'background.paper', // Use theme's paper background color
                p: 1, // Padding inside the card
                boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.3)', // Subtle shadow
                border: '1px solid rgba(255, 255, 255, 0.12)', // Border color
                background: "none"
            }}
        >
            <CardContent>
                <Typography variant="h5" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {generateMode ? 'Generate New Wallet' : 'Import Existing Wallet'}
                </Typography>
                <Typography sx={{ mb: 2 }}>
                    {generateMode
                        ? 'Enter a name for your new wallet.'
                        : 'Enter a name and a key for the wallet you want to import.'}
                </Typography>

                {/* Toggle Switch */}
                <FormControlLabel
                    control={
                        <Switch
                            checked={generateMode}
                            onChange={handleToggleChange}
                            name="generateModeSwitch"
                            color="secondary" // Use theme's secondary color
                            sx={{
                                '& .MuiSwitch-thumb': {
                                    backgroundColor: generateMode ? 'secondary' : '#f50057', // Change thumb color based on mode
                                },
                                '& .MuiSwitch-track': {
                                    backgroundColor: generateMode ? 'secondary' : '#f50057', // Change track color based on mode
                                },
                            }}
                        />
                    }
                    label="Generate a new wallet instead?"
                    sx={{ mb: 2, display: 'block', color: 'text.secondary' }} // Make it block to take full width if needed and style label
                />

                {/* Wallet Name Field (Always Visible) */}
                <Typography variant="body2" sx={{ mt: 1, mb: 1, fontWeight: 'medium' }}>
                    Wallet Name
                </Typography>
                <TextField
                    fullWidth
                    required // Make name required in both modes
                    variant="outlined"
                    placeholder="Enter wallet name..."
                    value={walletName}
                    onChange={(e) => setWalletName(e.target.value)}
                // sx={commonTextFieldStyles}
                />

                {/* 
                <FormControl fullWidth>
                    <Typography variant="body2" sx={{ mt: 2, mb: 1, fontWeight: 'medium', }}>
                        Wallet
                    </Typography>
                    <Select
                        required
                        id="wallet"
                        value={type}
                        label="Wallet"
                        onChange={(e) => setType(e.target.value)}
                        variant="outlined"
                        displayEmpty

                    >
                        <MenuItem value="" disabled>
                            Wallet type
                        </MenuItem>
                        <MenuItem value="BTC">BTC</MenuItem>
                        <MenuItem value="ICP">ICP</MenuItem>
                    </Select>
                </FormControl> */}

                <FormControl required variant="outlined" fullWidth> {/* Consider fullWidth */}
                    <Typography variant="body2" sx={{ mt: 2, mb: 1, fontWeight: 'medium', }}>
                        Wallet
                    </Typography>
                    <Select
                        labelId="wallet-select-label" // <-- Associate with InputLabel
                        id="wallet-select"          // <-- Make sure ID is unique if needed elsewhere
                        value={type}
                        label="Wallet" // <-- Now works correctly with InputLabel
                        onChange={(e) => setType(e.target.value)}
                        displayEmpty
                    >
                        <MenuItem value="" disabled style={{
                            background: "rgba(0,0,0, 1)", // Optional: Style for the placeholder
                            color: "white",
                            "&:selected": {
                                background: "rgba(0,0,0, 1)", // Optional: Style for the selected placeholder
                            },
                            "&:hover": {
                                background: "rgba(0,0,0, 1)", // Optional: Style for the hovered placeholder
                            }
                        }}>
                            {/* Using <em> is a common pattern for placeholder styling */}
                            <em>Wallet type</em>
                        </MenuItem>
                        <MenuItem value="BTC">BTC</MenuItem>
                        <MenuItem value="ICP">ICP</MenuItem>
                    </Select>
                    {/* Optional: Add helper text below */}
                </FormControl>
                {/* Wallet Address Field (Conditionally Visible) */}
                {!generateMode && ( // Only show if NOT in generateMode
                    <>
                        <Typography variant="body2" sx={{ mt: 2, mb: 1, fontWeight: 'medium', }}>
                            Wallet key
                        </Typography>
                        <TextField
                            fullWidth
                            required // Make address required when tracking
                            variant="outlined"
                            placeholder="Enter wallet key..."
                            value={walletKey}
                            onChange={(e) => setWalletKey(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="paste wallet key"
                                            onClick={handlePaste}
                                            edge="end"
                                            sx={{ color: 'text.secondary' }}
                                        >
                                            <ContentCopyIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        // sx={commonTextFieldStyles}
                        />
                    </>
                )}

                {/* Submit Button */}
                <Button
                    fullWidth
                    variant="contained"
                    onClick={handleSubmit}
                    // Disable button if required fields are empty
                    color="secondary" // Use theme's secondary color
                    disabled={
                        !walletName || (!generateMode && !walletKey)
                    }
                    sx={{
                        mt: 2, // Adjusted margin top
                        py: 1.5, // Vertical padding
                        borderRadius: 2, // Rounded corners
                        textTransform: 'none', // Prevent uppercase text
                        fontWeight: 'bold',
                        fontSize: '1rem',
                        color: 'common.white', // White text
                        // Apply the gradient background
                        // background: 'linear-gradient(to right, #FFA726, #F57C00)', // Orange gradient

                    }}
                >
                    {generateMode ? 'Generate Wallet' : 'Add Wallet'}
                </Button>
            </CardContent>
        </Card>
    );
}

export default WalletManagerForm;